import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CustomStep, DropdownComponent, Tick } from 'marketpro-design-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../auth/services/auth.service';
import { RangeFilter } from '../../../models/custom-filter-range.model';
import { SpendingGroup } from '../../../models/spending-group.model';
import { DataFiltersService } from '../../../services/data-filters.service';
import { SystemGroupsService } from '../../../services/system-groups.service';
import { AuthorizedResourcesService } from '../../../../auth/services/authorized-resources.service';
import { IncomeGroup } from '../../../models/income-group.model';
import { faLock, faPlus } from '@fortawesome/free-solid-svg-icons';
import { UpsaleModalService } from '../../../services/upsale-modal.service';
import { DashboardStatisticsService } from '../../../services/dashboard-statistics.service';
import { RangeFilterType } from '../../../models/range-filter-type.enum';

@Component({
  selector: 'mpro-spending-income-selector',
  templateUrl: './spending-income-selector.component.html',
  styleUrls: [
    './../dropdown-common.scss',
    './spending-income-selector.component.scss',
  ],
})
export class SpendingIncomeSelectorComponent implements OnInit, OnDestroy {
  @Input() public inputValue: string = '';
  @Input() public spendingGroups: SpendingGroup[] = [];
  @Input() public incomeGroups: IncomeGroup[] = [];
  @Input() public userSpendingGroups: SpendingGroup[] = [];
  @Input() public userIncomeGroups: IncomeGroup[] = [];
  @Input() public userGroups: {
    type: string;
    dropdownOpen: boolean;
    group: SpendingGroup[] | IncomeGroup[];
  }[];

  @ViewChild(DropdownComponent) public dropdown: DropdownComponent;
  public middleClassOpened: boolean;
  public inEditSpendingGroup: RangeFilter;
  public maxSpendingGroup: number = 131;
  public maxIncomeGroup: number = 46;
  public dataFilters;
  public isDeleteModalOpen: boolean = false;
  public spendingGroupForDeletion: SpendingGroup | IncomeGroup | undefined =
    undefined;
  public openedGroup: SpendingGroup = new SpendingGroup();
  public faPlus = faPlus;
  public faLock = faLock;
  public rangeFilterType = RangeFilterType;

  addNew = {
    isOpen: false,
    group: '',
  };
  userGroupsDropdownOpened = true;
  labelFormat: (label: string) => string;
  public customGroupSteps: CustomStep[] = [
    {
      from: 0,
      to: 131,
      step: 1,
      labelGap: 1,
      customLabels: {
        0: '$0',
        5: '$5',
        8: '$8',
        12: '$12',
        30: '$30',
        50: '$50',
        70: '$70',
        90: '$90',
        110: '$110',
        131: '>$130',
      },
      considerToValueAsUndefined: true,
    },
  ];
  public customIncomeSteps: CustomStep[] = [
    {
      from: 0,
      to: 46,
      step: 1,
      labelGap: 5,
      customLabels: {
        0: '$0',
        5: '$5',
        10: '$10',
        15: '$15',
        20: '$20',
        25: '$25',
        30: '$30',
        35: '$35',
        40: '$40',
        46: '>$45',
      },
      considerToValueAsUndefined: true,
    },
  ];
  // might not need 2 emitters
  @Output() public groupClicked: EventEmitter<SpendingGroup | IncomeGroup> =
    new EventEmitter();
  @Output() public subgroupClicked: EventEmitter<SpendingGroup | IncomeGroup> =
    new EventEmitter();
  @Output() public spendingGroupsChanged: EventEmitter<void> =
    new EventEmitter();
  @Output() public newGroup: EventEmitter<void> = new EventEmitter();

  private ngUnsubscribe: Subject<void> = new Subject();

  constructor(
    public authResources: AuthorizedResourcesService,
    public systemGroupsService: SystemGroupsService,
    public authService: AuthService,
    private dataFiltersService: DataFiltersService,
    private dashboardService: DashboardStatisticsService,
    private upsaleModalService: UpsaleModalService
  ) {}

  public ngOnInit() {
    this.dataFiltersService
      .getDataFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((filters) => (this.dataFilters = filters));

    this.labelFormat = (label) => {
      const numberValue = label.match(/\d+/)?.[0] || '0';
      if (numberValue === '0') {
        return `$${numberValue}`;
      }
      if (this.addNew.group === RangeFilterType.INCOME) {
        return numberValue === String(this.maxIncomeGroup)
          ? `>$${numberValue}k`
          : `$${numberValue}k`;
      }
      return numberValue === String(this.maxSpendingGroup)
        ? `>$${numberValue}`
        : `$${numberValue}`;
    };
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public onGroupClicked(group: SpendingGroup | IncomeGroup, subGroup: boolean) {
    if (!group.disabled || !group.authDisabled) {
      this.inputValue = group.displayValue();
      this.dropdown.dropdownOpen = false;
      if (subGroup) {
        this.subgroupClicked.emit(group);
      } else {
        this.groupClicked.emit(group);
      }
    }
  }

  private closeCreateWindowAndReloadGroups() {
    this.setAddNew(false);
    this.spendingGroupsChanged.emit();
  }

  onSave(rangeFilter: RangeFilter) {
    const tempRangeFilter = JSON.parse(JSON.stringify(rangeFilter));
    tempRangeFilter.type = this.addNew.group;
    if (this.addNew.group === RangeFilterType.INCOME) {
      tempRangeFilter.greaterThan = tempRangeFilter.greaterThan
        ? tempRangeFilter.greaterThan * 1000
        : undefined;
      tempRangeFilter.lessThan = tempRangeFilter.lessThan
        ? tempRangeFilter.lessThan * 1000
        : undefined;
    }
    if (tempRangeFilter.id) {
      this.dashboardService
        .updateRangeCustomFilter(tempRangeFilter)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => this.closeCreateWindowAndReloadGroups());
      return;
    }

    this.dashboardService
      .saveRangeCustomFilter(tempRangeFilter)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.closeCreateWindowAndReloadGroups());
  }

  public onPreview(rangeFilter: RangeFilter) {
    let group: SpendingGroup | IncomeGroup;
    const tempRangeFilter = JSON.parse(JSON.stringify(rangeFilter));
    tempRangeFilter.type = this.addNew.group;
    if (this.addNew.group === RangeFilterType.INCOME) {
      tempRangeFilter.greaterThan = tempRangeFilter.greaterThan
        ? tempRangeFilter.greaterThan * 1000
        : undefined;
      tempRangeFilter.lessThan = tempRangeFilter.lessThan
        ? tempRangeFilter.lessThan * 1000
        : undefined;
      group = IncomeGroup.from(tempRangeFilter);
      group.name = 'Income';
    } else {
      group = SpendingGroup.from(rangeFilter);
    }
    group.preview = true;
    this.groupClicked.emit(group);
    this.dropdown.toggleDropdown();
  }

  public onCancel() {
    this.setAddNew(false);
    this.inEditSpendingGroup = new RangeFilter('', 45, 85);
    if (this.dataFiltersService.getDataFiltersValue().spendingGroup.preview) {
      this.groupClicked.emit(this.spendingGroups[0]);
    }
  }

  public isGroupSelected(group: SpendingGroup) {
    if (group.id != undefined) {
      return (
        group.id ===
        this.dataFiltersService.getDataFiltersValue().spendingGroup.id
      );
    }

    return group.displayValue() === this.inputValue;
  }

  public onDropdownOpened() {
    this.spendingGroupsChanged.emit();
  }

  public userGroupsClicked(group) {
    group.dropdownOpen = !group.dropdownOpen;
  }

  public groupArrowClicked(group: SpendingGroup, showSubgroups: boolean) {
    this.openedGroup.opened = false;
    group.opened = showSubgroups;
    this.openedGroup = group;
  }

  public toggleDropdowns(dropdowns: DropdownComponent[]) {
    //Open first dropdown from array
    dropdowns.forEach((dropdown, index) => {
      dropdown.toggleDropdownWithParam(!index);
    });
  }

  public hideDropdowns(dropdowns: DropdownComponent[]) {
    dropdowns.forEach((dropdown) => (dropdown.dropdownOpen = false));
  }

  public onDeleteClicked(spendingGroup: SpendingGroup) {
    if (spendingGroup.id === undefined) {
      return;
    }

    this.spendingGroupForDeletion = spendingGroup;
    this.isDeleteModalOpen = true;
  }

  setAddNew(
    state: boolean,
    group?: RangeFilterType.SPENDING | RangeFilterType.INCOME
  ) {
    if (!this.authService.isLoggedIn() && state) {
      this.upsaleModalService.open();
      return;
    }
    this.addNew = {
      isOpen: state,
      group: group || '',
    };
    this.inEditSpendingGroup =
      group === RangeFilterType.SPENDING
        ? new RangeFilter('', 45, 85)
        : new RangeFilter('', 20, 40);
  }

  public onEditClicked(group: SpendingGroup | IncomeGroup) {
    this.setAddNew(
      true,
      group instanceof SpendingGroup
        ? RangeFilterType.SPENDING
        : RangeFilterType.INCOME
    );
    this.inEditSpendingGroup = RangeFilter.fromSpendingIncomeGroup(group);
  }

  public onDelete() {
    if (!this.spendingGroupForDeletion || !this.spendingGroupForDeletion.id) {
      return;
    }

    this.isDeleteModalOpen = false;
    this.dashboardService
      .deleteRangeCustomFilter(this.spendingGroupForDeletion.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.spendingGroupForDeletion = undefined;
        this.spendingGroupsChanged.emit();
      });
  }
}
